import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders, useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { canEditModule } from '../lib/permissions';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Upload, RefreshCw, AlertTriangle, CheckCircle2, MapPin, Wand2 } from 'lucide-react';
import SKUMappingDialog from '../components/SKUMappingDialog';
import { ImportPreviewDialog } from '../components/ImportPreviewDialog';
import { LoadError, errorMessage } from '../components/LoadError';
import { StatusBadge } from '../components/StatusBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SummaryCard = ({ label, qty, cost, accent = '#111', testId }) => (
  <div className="sp-card sp-card-lift rounded-xl p-4" data-testid={testId}>
    <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-500 mb-2">{label}</div>
    <div className="sp-num text-[24px] font-bold leading-none" style={{ color: accent }}>{qty?.toLocaleString('en-IN') ?? 0}</div>
    <div className="text-[11px] text-gray-500 mt-1.5">₹{(cost || 0).toLocaleString('en-IN')}</div>
  </div>
);

const InfoField = ({ label, value }) => (
  <div>
    <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-gray-500">{label}</div>
    <div className="text-[13px] font-medium text-gray-800 mt-0.5">{value || '—'}</div>
  </div>
);

export default function PODetailPage() {
  const { poNumber } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEditRetailPos = canEditModule(user, 'retail_pos');
  const canEditMappings = canEditModule(user, 'sku_aliases');
  const { country } = useCountry();
  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [mappingOpen, setMappingOpen] = useState(false);
  // Dry-run preview of a line-item (re-)import awaiting confirmation: { file, data }
  const [itemsPreview, setItemsPreview] = useState(null);
  const fileRef = useRef(null);

  const fetchPO = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/retail-pos/${poNumber}?country=${country}`, { headers: getAuthHeaders() });
      setPo(res.data);
      setLoadError(null);
    } catch (e) {
      if (e.response?.status === 404) {
        // Genuine "not found": fall through to the not-found screen.
        setPo(null);
        setLoadError(null);
      } else {
        setLoadError(e);
      }
    } finally {
      setLoading(false);
    }
  }, [poNumber, country]);

  useEffect(() => { fetchPO(); }, [fetchPO]);

  const retryFetch = () => { setLoading(true); fetchPO(); };

  const postItems = (file, dryRun) => {
    const form = new FormData();
    form.append('file', file);
    const params = new URLSearchParams({ country });
    if (dryRun) params.set('dry_run', 'true');
    return axios.post(`${API}/retail-pos/${poNumber}/import-items?${params}`, form, { headers: getAuthHeaders() });
  };

  // Step 1: picking a file only previews (dry run); nothing is saved yet.
  const handleItemsImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const res = await postItems(file, true);
      setItemsPreview({ file, data: res.data });
    } catch (err) {
      toast.error(errorMessage(err, 'Items import failed'));
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  // Step 2: re-send the same file for real. Throwing keeps the preview open.
  const confirmItemsImport = async () => {
    if (!itemsPreview) return;
    try {
      const res = await postItems(itemsPreview.file, false);
      toast.success(res.data.message);
      if (res.data.unmatched?.length > 0) {
        toast.warning(`${res.data.unmatched.length} items not in SKU Master — listed in the table`);
      }
      fetchPO();
    } catch (err) {
      toast.error(errorMessage(err, 'Items import failed'));
      throw err;
    }
  };

  if (loadError) return (
    <div className="space-y-4 sp-body py-6" data-testid="po-detail-load-error-page">
      <Button variant="outline" onClick={() => navigate('/retail-pos')} className="h-9 text-[13px]" data-testid="po-detail-error-back-btn">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>
      <LoadError what={`PO ${poNumber}`} error={loadError} onRetry={retryFetch} retrying={loading} testId="po-detail-load-error" />
    </div>
  );
  if (loading) return <div role="status" className="text-center py-16 text-gray-500 sp-body text-[13px]">Loading PO…</div>;
  if (!po) return (
    <div className="text-center py-16" data-testid="po-detail-not-found">
      <p className="text-gray-500 text-[14px]">PO {poNumber} not found</p>
      <Button variant="outline" onClick={() => navigate('/retail-pos')} className="mt-4 h-9 text-[13px]">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>
    </div>
  );

  const items = po.items || [];
  const unmatched = items.filter(i => i.sku_matched === false);

  return (
    <div className="space-y-5 sp-body" data-testid="po-detail-page">
      {/* Header */}
      <div className="sp-rise flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/retail-pos')} className="h-9 w-9 shrink-0 rounded-lg mt-1" aria-label="Back to Retail POs" data-testid="back-to-po-list-btn">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </Button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl break-all">{po.po_number}</h1>
              <StatusBadge status={po.status} data-testid="po-detail-status" />
            </div>
            <p className="mt-1 text-[13px] text-gray-500 flex flex-wrap items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" aria-hidden="true" /> Ship to <span className="sp-num font-semibold text-gray-700">{po.ship_to_fc}</span>
              <span className="text-gray-400 mx-1" aria-hidden="true">/</span> Ordered {po.order_date}
            </p>
          </div>
        </div>
        <div>
          {canEditRetailPos && (
          <>
          <input ref={fileRef} type="file" accept=".xls,.xlsx" onChange={handleItemsImport} className="hidden" data-testid="po-items-file-input" />
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={importing}
            className="h-9 px-4 bg-[#007AFF] hover:bg-[#005bb5] active:scale-95 text-white text-[13px] font-semibold rounded-lg transition-transform"
            data-testid="import-items-btn"
          >
            {importing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            {po.items_imported ? 'Re-import Line Items' : 'Import Line Items'}
          </Button>
          </>
          )}
        </div>
      </div>

      {/* Info block */}
      <div className="sp-card sp-rise rounded-xl p-5" style={{ animationDelay: '40ms' }} data-testid="po-info-block">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-x-6 gap-y-4">
          <InfoField label="Vendor" value={po.vendor_code} />
          <InfoField label="Ship-to FC" value={po.ship_to_fc} />
          <InfoField label="Ship Window" value={po.window_start ? `${po.window_start} → ${po.window_end}` : null} />
          <InfoField label="Cancellation Deadline" value={po.cancellation_deadline} />
          <InfoField label="Freight Terms" value={po.freight_terms} />
          <InfoField label="Currency" value={po.currency} />
          <InfoField label="PO Items" value={po.po_items_count} />
        </div>
      </div>

      {/* Summary counts */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="po-summary-cards">
        <SummaryCard label="Requested" qty={po.requested_qty} cost={po.total_requested_cost} testId="summary-requested" />
        <SummaryCard label="Accepted" qty={po.accepted_qty} cost={po.total_accepted_cost} accent="#007AFF" testId="summary-accepted" />
        <SummaryCard label="Received" qty={po.received_qty} cost={po.total_received_cost} accent="#15803D" testId="summary-received" />
        <SummaryCard label="Cancelled" qty={po.cancelled_qty} cost={po.total_cancelled_cost} accent="#B91C1C" testId="summary-cancelled" />
        <SummaryCard label="Remaining" qty={po.remaining_qty} cost={(po.total_accepted_cost || 0) - (po.total_received_cost || 0)} accent="#B45309" testId="summary-remaining" />
      </div>

      {/* Unmatched banner */}
      {unmatched.length > 0 && (
        <div className="sp-rise rounded-xl border border-[#FFCC00]/40 bg-[#FFFBEB] px-4 py-3 flex flex-wrap items-start justify-between gap-3" data-testid="unmatched-banner">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="h-4 w-4 text-[#B45309] mt-0.5 shrink-0" aria-hidden="true" />
            <p className="text-[12px] text-[#92400E]">
              <span className="font-bold">{unmatched.length} line item(s) not found in SKU Master</span> — {canEditMappings
                ? 'map them to inventory SKUs so they get planned & shipped.'
                : 'they need mapping to inventory SKUs before they can be planned. Ask a manager or admin to fix the mappings.'}
            </p>
          </div>
          {canEditMappings && (
          <Button
            onClick={() => setMappingOpen(true)}
            className="h-8 px-3 bg-[#B45309] hover:bg-[#92400E] text-white text-[11px] font-semibold rounded-md"
            data-testid="open-sku-mapping-btn"
          >
            <Wand2 className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" /> Fix Mappings
          </Button>
          )}
        </div>
      )}

      <ImportPreviewDialog
        open={!!itemsPreview}
        onOpenChange={(open) => { if (!open) setItemsPreview(null); }}
        title={items.length > 0
          ? `Replace all ${items.length} existing line item${items.length === 1 ? '' : 's'} on ${po.po_number}?`
          : `Review line items for ${po.po_number}`}
        fileName={itemsPreview?.file?.name}
        preview={itemsPreview?.data}
        entityLabel="line items"
        issuesNote="Rows with errors are skipped; lines whose code is not found are imported without a SKU match."
        confirmLabel={items.length > 0 ? 'Replace line items' : 'Import line items'}
        onConfirm={confirmItemsImport}
        testId="po-items-import-preview"
      />

      <SKUMappingDialog
        open={mappingOpen}
        onOpenChange={setMappingOpen}
        unmatched={unmatched.map(u => ({
          external_id: u.external_id,
          asin: u.asin,
          model_number: u.model_number,
          title: u.title,
          suggestions: u.match_suggestions || [],
        }))}
        country={country}
        poNumber={poNumber}
        onApplied={fetchPO}
      />

      {/* Line items */}
      <div className="sp-card sp-rise rounded-xl overflow-hidden shadow-sm" style={{ animationDelay: '100ms' }}>
        <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 border-b border-black/5 bg-gray-50/70">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500">Line Items ({items.length})</span>
          {po.items_imported && (
            <span className="text-[11px] text-gray-600">
              <span className="sp-num font-semibold text-green-700">{po.matched_items}</span> matched · <span className="sp-num font-semibold text-amber-700">{po.unmatched_items}</span> unmatched
            </span>
          )}
        </div>
        {items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px] sp-grid-table" data-testid="po-items-table">
              <thead>
                <tr className="text-gray-500">
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]">ASIN</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]">SKU (External Id)</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]">Title</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]">Expected</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]">Req</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]">Acc</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]">ASN</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]">Rcvd</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]">Outstanding</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]">Unit ₹</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]">Total ₹</th>
                  <th className="text-center px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]">Match</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={`${it.external_id}-${it.asin}`} data-testid={`po-item-row-${i}`}>
                    <td className="px-4 py-2.5 sp-num text-[11px] text-gray-500">{it.asin}</td>
                    <td className="px-4 py-2.5 sp-num font-semibold text-gray-800">{it.external_id}</td>
                    <td className="px-4 py-2.5 text-gray-500 text-[11px] max-w-[260px] truncate">{it.title}</td>
                    <td className="px-4 py-2.5 text-gray-500">{it.expected_date}</td>
                    <td className="px-4 py-2.5 text-right sp-num text-gray-500">{it.requested_qty}</td>
                    <td className="px-4 py-2.5 text-right sp-num font-semibold">{it.accepted_qty}</td>
                    <td className="px-4 py-2.5 text-right sp-num text-gray-500">{it.asn_qty}</td>
                    <td className="px-4 py-2.5 text-right sp-num text-gray-500">{it.received_qty}</td>
                    <td className="px-4 py-2.5 text-right sp-num font-bold text-amber-700">
                      {it.outstanding_qty}
                      {it.planned_qty > 0 && (
                        <div className="mt-0.5 text-[10px] font-normal text-gray-500 whitespace-nowrap" data-testid={`po-item-planned-${i}`}>
                          {it.planned_qty} in{' '}
                          <a
                            href="/shipment-planning?tab=shipments"
                            onClick={(e) => { e.preventDefault(); navigate('/shipment-planning?tab=shipments'); }}
                            className="text-[#0062CC] underline"
                            title={(it.planned_shipment_ids || []).join(', ') || undefined}
                          >
                            FC shipments
                          </a>
                          {' · '}{it.dispatchable_qty ?? 0} dispatchable here
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right sp-num">{(it.unit_cost || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-2.5 text-right sp-num">{(it.total_cost || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-2.5 text-center">
                      {it.sku_matched ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600 inline" role="img" aria-label="Matched to SKU Master" />
                      ) : (
                        <AlertTriangle className="h-4 w-4 text-amber-600 inline" role="img" aria-label="Not in SKU Master" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500 text-[13px]" data-testid="no-items-placeholder">
            No line items imported yet. Upload the PurchaseOrder file for this PO to see ASIN-level details.
          </div>
        )}
      </div>
    </div>
  );
}
